import { buildCollection } from "@firecms/core";

import { cleanUpReplacedUploads } from "../uploadCleanup";
import {
  MAX,
  cards,
  ctaLabel,
  eyebrow,
  heading,
  paragraphs,
  photoRow,
  quote,
  section,
  subheading,
} from "./sections";

// The Home page: one document (`pages/home`). Sections are declared in page
// order. The counters, card symbols, paw prints and button targets stay in the
// site's code; see docs/cms-content-model.md.

export const homePageCollection = buildCollection({
  id: "page-home",
  path: "pages",
  name: "Home",
  singularName: "Home page",
  icon: "Home",
  group: "Pages",
  description:
    "Every piece of text on the Home page, listed in the order it appears on the page.",
  // `pages` holds one document per page; this collection shows only Home's.
  forceFilter: { pageId: ["==", "home"] },
  // The page exists already and the site expects it — it can be edited, but
  // not added to or removed.
  permissions: { read: true, edit: true, create: false, delete: false },
  openEntityMode: "full_screen",
  hideIdFromForm: true,
  // Hidden from the drawer; the `pageShortcut` view in App.tsx opens it directly.
  hideFromNavigation: true,
  inlineEditing: false,
  exportable: false,
  // Replacing a photo leaves the old file in the bucket unless we remove it.
  callbacks: cleanUpReplacedUploads,

  properties: {
    hero: section(
      "1. The welcome at the top",
      "Your name, what you do, and the big line underneath it — the very first thing a visitor reads.",
      {
        name: {
          name: "Your name",
          dataType: "string",
          description: "Shown in large hand-lettering at the very top of the page.",
          validation: { max: MAX.heading },
        },
        role: {
          name: "What you do",
          dataType: "string",
          description:
            "The small line in capitals under your name, e.g. Animal Communicator and Energy Healing.",
          validation: { max: MAX.heading },
        },
        taglineTop: {
          name: "Big line — first part",
          dataType: "string",
          description: "The opening of the large sentence, e.g. Giving animals a voice.",
          validation: { max: MAX.heading },
        },
        taglineAccent: {
          name: "Big line — middle part, in colour",
          dataType: "string",
          description:
            "The middle of the same sentence. This part is shown in the brighter purple, so keep it short.",
          validation: { max: MAX.heading },
        },
        taglineBottom: {
          name: "Big line — last part",
          dataType: "string",
          description: "How the large sentence finishes, e.g. a deeper connection with your animal.",
          validation: { max: MAX.heading },
        },
        primaryCtaLabel: ctaLabel(
          "The wording on the filled button. It goes to the Services page.",
          "Main button text",
        ),
        secondaryCtaLabel: ctaLabel(
          "The wording on the plainer button beside it. It goes to the About Me page.",
          "Second button text",
        ),
      },
    ),

    photoStrip: section(
      "2. The row of three photos",
      "The wide band of photos just below the buttons.",
      {
        items: photoRow({
          name: "The three photos",
          description: "They sit side by side, left to right.",
          count: 3,
          storagePath: "pages/home/photoStrip",
          shape:
            "It is cropped to a square on a computer and to a tall rectangle on a phone, so use the focus settings to say which part must stay in view.",
        }),
      },
    ),

    intro: section(
      "3. Have you ever wished...",
      "The cream panel that introduces your work.",
      {
        heading: heading("The question in large hand-lettering at the top of the panel."),
        lead: paragraphs(
          "Opening paragraphs",
          "These come first, before the highlighted line below.",
        ),
        highlight: {
          name: "Highlighted line",
          dataType: "string",
          description:
            "The single short line shown larger and in colour, e.g. You're in the right place.",
          validation: { max: MAX.subheading },
        },
        body: paragraphs(
          "Paragraphs after the highlighted line",
          "These follow the highlighted line, in this order.",
        ),
        callout: {
          name: "Closing box",
          dataType: "string",
          multiline: true,
          description: "The paragraph in the tinted box that closes this panel.",
          validation: { max: MAX.paragraph },
        },
      },
    ),

    philosophy: section(
      "4. Animals Are Here to Teach Us About Love",
      "The green band of small white boxes.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        body: paragraphs(
          "The boxes",
          "Each box becomes one white box on the page, filling two columns left to right. An even number keeps the last row full.",
        ),
      },
    ),

    services: section(
      "5. How I Can Help",
      "The three service cards and the button beneath them. The little symbol and the colour of each card are part of the layout and stay in the site.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        items: cards({
          name: "The three services",
          description: "Each card has a title and a short paragraph.",
          count: 3,
          previewProperties: ["title"],
          properties: {
            title: {
              name: "Card title",
              dataType: "string",
              validation: { max: MAX.cardTitle },
            },
            body: {
              name: "Card text",
              dataType: "string",
              multiline: true,
              validation: { max: MAX.cardBody },
            },
          },
        }),
        ctaLabel: ctaLabel("The wording on the button below the cards. It goes to the Services page."),
      },
    ),

    youBelongHere: section(
      "6. You Don't Need to Have All the Answers",
      "The list of questions people arrive with, and the reassuring panel underneath.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        subheading: subheading("The line that introduces the questions, e.g. Many clients come to me wondering:"),
        questions: paragraphs(
          "The questions",
          "Each box is one question, shown with a star beside it. They fill two columns left to right. Add, reorder or remove them freely.",
        ),
        highlight: {
          name: "Highlighted line",
          dataType: "string",
          multiline: true,
          description:
            "The bold centred line at the top of the panel below the questions.",
          validation: { max: MAX.subheading },
        },
        body: paragraphs(
          "Paragraphs in the panel",
          "These sit under the highlighted line, inside the same panel.",
        ),
      },
    ),

    safeSpace: section(
      "7. A Safe Space for Both You and Your Animal",
      "Your promise to a visitor, as a list of points followed by a quote.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        body: paragraphs(
          "The points",
          "Each box becomes one point on the page, shown with a star. Add, reorder or remove them freely.",
        ),
        quote: quote(false, "The quote at the end"),
      },
    ),

    readyToConnect: section(
      "8. Ready to Connect?",
      "The closing invitation at the very bottom, with the two buttons and the visitors' comments underneath.",
      {
        heading: heading("The closing invitation, e.g. Ready to Connect?"),
        body: {
          name: "Closing line",
          dataType: "string",
          multiline: true,
          description: "The short paragraph between the invitation and the buttons.",
          validation: { max: MAX.paragraph },
        },
        ctaLabel: ctaLabel(
          "The wording on the filled button. It goes to the Services page.",
          "Main button text",
        ),
        secondaryCtaLabel: ctaLabel(
          "The wording on the plainer button beside it. It goes to the Resources page.",
          "Second button text",
        ),
        testimonialsTitle: {
          name: "Title above the comments",
          dataType: "string",
          description:
            "The heading over the rotating comments from other people, e.g. Others' Experiences. The comments themselves are edited under Testimonials.",
          validation: { max: MAX.heading },
        },
      },
    ),

    // Identifies which page this document is; set once when seeded.
    pageId: {
      name: "Page id",
      dataType: "string",
      disabled: { hidden: true },
    },
    title: {
      name: "Page title",
      dataType: "string",
      disabled: { hidden: true },
    },
  },

  // The order of the panels in the form — matches the live page top to bottom.
  propertiesOrder: [
    "hero",
    "photoStrip",
    "intro",
    "philosophy",
    "services",
    "youBelongHere",
    "safeSpace",
    "readyToConnect",
  ],
});
